import React, { FC } from 'react';
import { startOfDay } from 'date-fns';
import ReminderModalBase from './ReminderModalBase';
import { BaseReminderModalProps, ReminderColumnConfig, ReminderConfig, ScheduleRelation } from './types';
import { ReminderDateTypeLabel } from 'containers/settings/constants';
import DateUtils from 'utils/dateUtils';
import { CustomInput, ListDropDown } from 'components';
import { InputType } from 'components/custom-input.tsx/CustomInput';
import { OutlinedInfoIcon } from 'assets/icons';
import { calculateScheduledDate } from 'containers/estimates/utils';
import { NotifierTypes } from 'constants/notifierConstants';
import { showNotification } from 'utils/Notifier';
import { hasDuplicateReminders } from 'containers/settings/utils';
import { useAppDispatch } from 'hooks';

interface EditReminderModalProps extends BaseReminderModalProps {
  // ISO date string (UTC) for calculating reminder dates (due date for invoices, expiry date for estimates)
  referenceDate?: string;
  dateType?: ReminderDateTypeLabel;
}

const EditReminderModal: FC<EditReminderModalProps> = ({
  title = 'Payment reminders',
  subtitle = 'Reminders will be sent to customers email',
  referenceDate,
  dateType = ReminderDateTypeLabel.EXPIRY_DATE,
  customColumns = [],
  initialReminders = [],
  ...props
}) => {
  const dispatch = useAppDispatch();

  // Define schedule relations priority based on date type (controls next reminder selection order)
  const scheduleRelationsPriority: ScheduleRelation[] = [
    ScheduleRelation.BEFORE,
    ScheduleRelation.ON,
    ...(dateType === ReminderDateTypeLabel.DUE_DATE ? [ScheduleRelation.AFTER] : [])
  ];

  // Process initial reminders to calculate dates
  const processedInitialReminders = initialReminders.map(reminder => ({
    ...reminder,
    reminderDate:
      reminder.reminderDate ||
      (referenceDate ? calculateScheduledDate(referenceDate, reminder.scheduleRelation, reminder.days) : null)
  }));

  const handleScheduleRelationChange = (
    reminder: ReminderConfig,
    newRelation: ScheduleRelation,
    onUpdate: (field: string, value: unknown) => void
  ) => {
    onUpdate('scheduleRelation', newRelation);

    // Determine new days value based on relation
    let newDays = reminder.days;
    if (newRelation === ScheduleRelation.ON) {
      newDays = 0;
    } else if (reminder.days === 0) {
      newDays = 1;
    }
    // Update days if it changed
    if (newDays !== reminder.days) {
      onUpdate('days', newDays);
    }

    // Recalculate reminderDate (used as scheduledDate) with the correct days value
    if (referenceDate) {
      const newDate = calculateScheduledDate(referenceDate, newRelation, newDays);
      onUpdate('reminderDate', newDate);
    }
  };

  const handleDaysChange = (
    reminder: ReminderConfig,
    value: string,
    onUpdate: (field: string, value: unknown) => void
  ) => {
    const isOnDateSelected = reminder.scheduleRelation === ScheduleRelation.ON;

    // For ON relation, days is always 0
    if (isOnDateSelected) {
      onUpdate('days', 0);
      // Recalculate reminderDate (used as scheduledDate)
      if (referenceDate) {
        const newDate = calculateScheduledDate(referenceDate, reminder.scheduleRelation, 0);
        onUpdate('reminderDate', newDate);
      }
      return;
    }

    // For BEFORE/AFTER: Allow empty string for clearing the field (temporarily set to 0 for display)
    if (value === '' || value === null || value === undefined) {
      onUpdate('days', 0);
      return;
    }

    const numValue = parseInt(value);
    // Prevent 0 and negative values, only allow positive numbers for BEFORE/AFTER
    if (!isNaN(numValue) && numValue > 0) {
      onUpdate('days', numValue);
      // Recalculate reminderDate (used as scheduledDate)
      if (referenceDate) {
        const newDate = calculateScheduledDate(referenceDate, reminder.scheduleRelation, numValue);
        onUpdate('reminderDate', newDate);
      }
    }
    // If user types 0, don't update (prevent 0 from being saved for BEFORE/AFTER)
  };

  const handleDaysBlur = (reminder: ReminderConfig, onUpdate: (field: string, value: unknown) => void) => {
    const isBeforeOrAfter =
      reminder.scheduleRelation === ScheduleRelation.BEFORE || reminder.scheduleRelation === ScheduleRelation.AFTER;

    // On blur, if value is 0 for BEFORE/AFTER (empty/cleared), set to minimum 1
    if (isBeforeOrAfter && reminder.days === 0) {
      onUpdate('days', 1);
      // Recalculate reminderDate with minimum 1
      if (referenceDate) {
        const newDate = calculateScheduledDate(referenceDate, reminder.scheduleRelation, 1);
        onUpdate('reminderDate', newDate);
      }
    }
  };

  // Default columns: Send + Days + Scheduled For
  const defaultColumns: ReminderColumnConfig[] = [
    {
      key: 'send',
      label: 'Send',
      width: '200px',
      render: (reminder: ReminderConfig, onUpdate) => {
        const scheduleRelationOptions = [
          { id: ScheduleRelation.BEFORE, name: `Before ${dateType}` },
          { id: ScheduleRelation.ON, name: `On ${dateType}` },
          ...(dateType === ReminderDateTypeLabel.DUE_DATE
            ? [{ id: ScheduleRelation.AFTER, name: `After ${dateType}` }]
            : [])
        ];

        return (
          <ListDropDown
            id={`schedule-relation-${reminder.id}`}
            selectedItem={scheduleRelationOptions.find(option => option.id === reminder.scheduleRelation)}
            items={scheduleRelationOptions}
            setSelectedItem={item => {
              handleScheduleRelationChange(reminder, item.id as ScheduleRelation, onUpdate);
            }}
            buttonStyles="bg-highlightGray px-3 py-2.5 text-sbase"
            iconStyle="right-3"
            optionStyles="min-w-fit"
            isPortal={true}
          />
        );
      }
    },
    {
      key: 'days',
      label: 'Days',
      width: '110px',
      render: (reminder: ReminderConfig, onUpdate) => {
        const isOnDateSelected = reminder.scheduleRelation === ScheduleRelation.ON;
        // Display empty string when days is 0 for BEFORE/AFTER (allows clearing), but show 0 for ON
        const displayValue = isOnDateSelected || reminder.days > 0 ? reminder.days.toString() : '';
        return (
          <CustomInput
            id={`days-${reminder.id}`}
            type={InputType.NUMBER}
            disabled={isOnDateSelected}
            value={displayValue}
            onChange={value => handleDaysChange(reminder, value, onUpdate)}
            onBlur={() => handleDaysBlur(reminder, onUpdate)}
            className="h-10 w-full px-3 py-2.5"
            inputStyle={`${isOnDateSelected ? 'opacity-60' : ''}`}
            placeholder=""
          />
        );
      }
    },
    {
      key: 'scheduled',
      label: 'Scheduled for',
      width: '150px',
      render: (reminder: ReminderConfig) => {
        // reminderDate is now used as scheduledDate
        const scheduledDate =
          reminder.reminderDate ||
          (referenceDate ? calculateScheduledDate(referenceDate, reminder.scheduleRelation, reminder.days) : null);

        return (
          <span className="text-sbase text-primaryText">
            {scheduledDate
              ? DateUtils.getDateInFormat({
                  date: DateUtils.parseISODateString(scheduledDate),
                  dateFormat: 'dd MMM yyyy'
                })
              : '-'}
          </span>
        );
      }
    }
  ];

  // Merge default columns with custom columns
  const allColumns = [...defaultColumns, ...customColumns];

  const handleSave = (reminders: ReminderConfig[]) => {
    if (hasDuplicateReminders(reminders)) {
      showNotification(
        dispatch,
        NotifierTypes.WARNING,
        'Duplicate reminders found. Please remove duplicate reminders.'
      );
      return;
    }
    props.onSave(reminders);
  };

  const handleNewReminderCreate = (baseReminder: ReminderConfig): ReminderConfig => {
    // Enrich the base reminder with calculated fields
    return {
      ...baseReminder,
      reminderDate: referenceDate
        ? calculateScheduledDate(referenceDate, baseReminder.scheduleRelation, baseReminder.days)
        : undefined
    };
  };

  const renderValidationRow = (reminder: ReminderConfig) => {
    const scheduledDate =
      reminder.reminderDate ||
      (referenceDate ? calculateScheduledDate(referenceDate, reminder.scheduleRelation, reminder.days) : null);
    const isDayPassed = startOfDay(DateUtils.parseISODateString(scheduledDate)) < startOfDay(DateUtils.getNow());

    if (scheduledDate && isDayPassed) {
      return (
        <div className="flex items-center gap-1.5 text-sbase text-[#E38A3C]">
          <OutlinedInfoIcon className="path path-fill-current h-[15px] w-[15px] shrink-0" />
          <span>Date has already passed</span>
        </div>
      );
    }
    return null;
  };

  const getFooterInfo = () => {
    const text =
      dateType === ReminderDateTypeLabel.DUE_DATE
        ? 'Once a payment request has been paid, reminders will no longer be sent'
        : 'Once an estimate request has been responded to or an invoice created, reminders will no longer be sent';

    return (
      <div className="flex items-center gap-2 text-sbase text-accentText">
        <OutlinedInfoIcon className="path path-fill-current h-4 w-4 shrink-0" />
        <span>{text}</span>
      </div>
    );
  };

  return (
    <ReminderModalBase
      {...props}
      title={title}
      subtitle={subtitle}
      columns={allColumns}
      initialReminders={processedInitialReminders}
      onSave={handleSave}
      renderValidationRow={renderValidationRow}
      scheduleRelationsPriority={scheduleRelationsPriority}
      isLoading={props.isLoading || false}
      onNewReminderCreate={handleNewReminderCreate}
      footerInfo={getFooterInfo()}
    />
  );
};

export default EditReminderModal;
